"use client";

import { Alert, Box, Button, Typography, CircularProgress, Chip } from "@mui/material";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import TextField from "@mui/material/TextField";
import { Formik, Form } from "formik";
import dynamic from 'next/dynamic';
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useSnackbar } from "notistack";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PublicGtm from "../components/PublicGtm";
import Wrapper from "../components/Wrapper";
import { getGoogleAuthUrl, login, confirmToken } from "../services/AuthService";
import { skipAuthorization } from "../services/AmazonServices";
import { setAuthUser } from "../store/slices/authUser";
import { colors } from "../theme/colors";
import { isInAppBrowser } from "../utils/inAppBrowser";
import { hasMarketplaceSkipOverride, syncMarketplaceSkipIfPending } from "../utils/marketplaceSkip";
const Yup = dynamic(() => import('yup'), {
  ssr: false
});
const SegmentedLanguageControl = dynamic(() => import("../components/SegmentedLanguageControl"), {
  ssr: false
});
const Login = () => {
  const g = useTranslations("general");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [browserChecked, setBrowserChecked] = useState(false);
  const [inAppBrowser, setInAppBrowser] = useState(false);
  useEffect(() => {
    setInAppBrowser(isInAppBrowser());
    setBrowserChecked(true);
  }, []);
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const theme = createTheme({
    typography: {
      fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
    }
  });
  const validationSchema = React.useMemo(() => {
    return require('yup').object({
      email: require('yup').string().email(g("emailValidationOnchange")).required(g("emptyEmailError")),
      pass: require('yup').string().min(6, g("passwordValidation")).required(g("emptyPasswordError"))
    });
  }, [g]);
  const handleSubmit = async (values, {
    setFieldError
  }) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setLoginError("");
    setLoading(true);
    try {
      const payload = {
        email: values.email,
        password: values.pass
      };
      await login(payload).then(response => {
        dispatch(setAuthUser(response.data?.user));
        localStorage.setItem("token", response.data?.user.token);
        syncMarketplaceSkipIfPending(skipAuthorization);
        enqueueSnackbar(g("loginSuccess"), {
          variant: "success",
          autoHideDuration: 2000
        });
        const localSkipOverride = hasMarketplaceSkipOverride();
        if (response.data?.user.redirection_url && !response.data?.user.store.skip_now && !localSkipOverride) {
          router.push("/amazon-market-place");
          return;
        }
        if (!response.data?.user.is_password_updated) {
          router.push("/on-boarding");
        } else {
          router.push("/dashboard");
        }
        setLoading(false);
      }).catch(e => {
        setLoading(false);
        if (e.response && e.response.data && e.response.data.errors) {
          const backendErrors = e.response.data.errors;
          backendErrors.forEach(err => {
            setFieldError(err.path, err.msg);
          });
        } else if (e.response && [401, 403].includes(e.response.status)) {
          setLoginError(e?.response?.data?.message || g("invalidCredentials"));
        } else if (!e.response || e.response.status !== 503) {
          setLoginError(e?.response?.data?.message || g("invalidCredentials"));
        }
      });
    } catch (error) {
      setLoading(false);
    }
  };
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const token = query.get("confirmationToken");
    if (token) {
      confirmEmailToken(token);
    }
  }, []);
  const confirmEmailToken = async token => {
    setLoading(true);
    try {
      const response = await confirmToken(token);
      enqueueSnackbar(response.message || g("Tokenconfirmed"), {
        variant: "success",
        autoHideDuration: 3000
      });
    } catch (error) {
      const isConfirmationNotFound = error?.response?.status === 404;
      enqueueSnackbar(error?.response?.data?.message || g("unexpectedError") || "Token Expired", {
        variant: "error",
        autoHideDuration: isConfirmationNotFound ? 7000 : 3000,
        action: isConfirmationNotFound ? () => <Button size="small" sx={{
          color: '#fff',
          textTransform: 'none',
          textDecoration: 'underline'
        }} onClick={() => router.push('/verify-email')}>
              Resend confirmation email
            </Button> : undefined
      });
    } finally {
      setLoading(false);
    }
  };
  const onGoogleLogin = async () => {
    setLoading(true);
    try {
      const {
        data,
        status
      } = await getGoogleAuthUrl();
      if (status === 200) {
        setLoading(false);
        router.push(data?.authorizeUrl);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  return <Wrapper skipAuth={true} data-sentry-element="Wrapper" data-sentry-component="Login" data-sentry-source-file="page.jsx">
      <PublicGtm data-sentry-element="PublicGtm" data-sentry-source-file="page.jsx" />
      <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-source-file="page.jsx">
        {/* Animated gradient background */}
        <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        zIndex: 0,
        background: `linear-gradient(135deg, #c44569 0%, #AC1218 50%, #8B0E14 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 107, 107, 0.25) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(255, 154, 158, 0.2) 0%, transparent 50%)
              `,
          animation: 'float 20s ease-in-out infinite'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-30%',
          right: '-30%',
          width: '150%',
          height: '150%',
          background: `
                radial-gradient(ellipse at 70% 70%, rgba(255, 154, 158, 0.2) 0%, transparent 60%),
                radial-gradient(ellipse at 30% 30%, rgba(250, 207, 239, 0.2) 0%, transparent 60%)
              `,
          animation: 'float 25s ease-in-out infinite reverse'
        },
        '@keyframes float': {
          '0%, 100%': {
            transform: 'translate(0px, 0px) rotate(0deg)'
          },
          '33%': {
            transform: 'translate(80px, -60px) rotate(120deg)'
          },
          '66%': {
            transform: 'translate(-50px, 50px) rotate(240deg)'
          }
        }
      }} data-sentry-element="Box" data-sentry-source-file="page.jsx" />
        <Box sx={{
        minHeight: {
          xs: '100svh',
          fallback: '100vh'
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: {
          xs: 'max(20px, env(safe-area-inset-top)) 20px max(40px, env(safe-area-inset-bottom))',
          sm: '40px 20px'
        },
        position: 'relative',
        zIndex: 1,
        boxSizing: 'border-box'
      }} data-sentry-element="Box" data-sentry-source-file="page.jsx">
          <Box sx={{
          width: '100%',
          maxWidth: '480px',
          backgroundColor: colors.background.paper,
          borderRadius: '16px',
          padding: '40px',
          boxShadow: colors.shadows.card,
          [theme.breakpoints.down('sm')]: {
            padding: '32px 24px'
          }
        }} data-sentry-element="Box" data-sentry-source-file="page.jsx">
            {/* Header */}
            <Box sx={{
            textAlign: "center",
            mb: 4
          }} data-sentry-element="Box" data-sentry-source-file="page.jsx">
              {/* Logo */}
              <Box sx={{
              mb: 3
            }} data-sentry-element="Box" data-sentry-source-file="page.jsx">
                <Image src="/assets/images/Descripio_horizontal.png" width={140} height={39} alt="Descripio Logo" style={{
                maxWidth: "100%",
                height: "auto"
              }} data-sentry-element="Image" data-sentry-source-file="page.jsx" />
              </Box>

              <Typography variant="h4" sx={{
              fontWeight: "700",
              color: colors.text.primary,
              mb: 1,
              fontSize: {
                xs: "24px",
                sm: "28px"
              }
            }} data-sentry-element="Typography" data-sentry-source-file="page.jsx">
                {g("loginTitle")}
              </Typography>
              <Typography variant="body1" sx={{
              color: colors.text.tertiary,
              fontSize: {
                xs: "14px",
                sm: "16px"
              }
            }} data-sentry-element="Typography" data-sentry-source-file="page.jsx">
                {g("DominateCustomerproductListing")}
              </Typography>
            </Box>
            <Formik initialValues={{
            email: "",
            pass: ""
          }} validationSchema={validationSchema} onSubmit={handleSubmit} data-sentry-element="Formik" data-sentry-source-file="page.jsx">
              {({
              values,
              handleChange,
              handleBlur,
              errors,
              touched
            }) => <Form>
                  {/* Inline error alert */}
                  {loginError && <Alert severity="error" onClose={() => setLoginError("")} sx={{
                mb: 2,
                borderRadius: '8px'
              }}>
                      {loginError}
                    </Alert>}
                  {/* Form Fields */}
                  <Box sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 3
              }}>
                    <TextField label={g("emailPlaceHolder")} name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email} fullWidth disabled={loading} inputProps={{
                  autoComplete: "username",
                  autoCapitalize: "none",
                  spellCheck: "false",
                  enterKeyHint: "next",
                  inputMode: "email"
                }} sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary.main
                    }
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary.main
                  }
                }} />
                    <TextField label={g("passwordPlaceHolder")} name="pass" type="password" value={values.pass} onChange={handleChange} onBlur={handleBlur} error={touched.pass && Boolean(errors.pass)} helperText={touched.pass && errors.pass} fullWidth disabled={loading} inputProps={{
                  autoComplete: "current-password",
                  enterKeyHint: "go",
                  inputMode: "text"
                }} sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary.main
                    }
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary.main
                  }
                }} />
                  </Box>
                  <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{
                height: "48px",
                backgroundColor: colors.cta.main,
                color: "white",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "8px",
                mb: 2,
                "&:hover": {
                  backgroundColor: colors.cta.hover
                },
                "&:disabled": {
                  backgroundColor: colors.interactive.disabled
                }
              }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : g("loginTitle")}
                  </Button>

                  <Box sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3
              }}>
                    <Link href="/verify-email" style={{
                  color: colors.text.tertiary,
                  textDecoration: "underline",
                  fontSize: "14px",
                  fontWeight: "500"
                }}>
                      {g("ForgetPasswordLink")}
                    </Link>
                  </Box>
                  {/* Divider */}
                  <Box sx={{
                display: "flex",
                alignItems: "center",
                mb: 3
              }}>
                    <Box sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: colors.border.light
                }} />
                    <Typography sx={{
                  px: 2,
                  color: colors.text.tertiary,
                  fontSize: "14px"
                }}>
                      {g("OR")}
                    </Typography>
                    <Box sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: colors.border.light
                }} />
                  </Box>
                  {/* In-app browser warning */}
                  {inAppBrowser && <Box sx={{
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: '#FFF3E0',
                border: '1px solid #FFE0B2'
              }}>
                      <Typography sx={{
                  fontSize: '13px',
                  color: '#E65100',
                  lineHeight: 1.4
                }}>
                        Google sign-in is not available in this browser. Please open this page in Safari or Chrome.
                      </Typography>
                    </Box>}
                  <Button fullWidth variant="outlined" onClick={onGoogleLogin} disabled={loading || !browserChecked || inAppBrowser} sx={{
                mb: 3,
                height: "48px",
                borderColor: colors.border.light,
                color: colors.text.secondary,
                backgroundColor: colors.background.paper,
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "500",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: colors.background.light,
                  borderColor: colors.border.medium
                }
              }} startIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>}>
                    {g("withgoogle")}
                  </Button>
                  {/* Sign Up Link */}
                  <Box sx={{
                textAlign: "center"
              }}>
                    <Typography sx={{
                  fontSize: "14px",
                  color: colors.text.tertiary
                }}>
                      {g("Donhaveaccount")} {" "}
                      <Link href="/signup" style={{
                    color: colors.primary.main,
                    textDecoration: "underline",
                    fontWeight: "500"
                  }}>
                        {g("SignUp")}
                      </Link>
                    </Typography>
                  </Box>

                  {/* Language Switcher - Bottom */}
                  <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${colors.border.light}`
              }}>
                    <SegmentedLanguageControl />
                  </Box>
                </Form>}
            </Formik>
          </Box>
        </Box>
      </ThemeProvider>
    </Wrapper>;
};
export default Login;